import type { XYPosition } from '@xyflow/react';
import type { IProjectAsset } from '@packages/global';

import type {
  ClipCanvasFormType,
  ClipCanvasNode,
  ClipCanvasNodeActions,
  ClipCanvasResourceMediaType,
} from './types';
import { getAssetVoiceAudioUrl } from './referenceMedia';

const SETTING_NODE_X = 3420;
const SETTING_NODE_Y = 0;
const SETTING_NODE_GAP_Y = 430;
const PREVIOUS_SETTING_NODE_X = 3220;
const MEDIA_RESOURCE_NODE_X = 3820;
const MEDIA_RESOURCE_NODE_Y = 0;
const MEDIA_RESOURCE_NODE_GAP_Y = 320;
const LEGACY_SETTING_NODE_X = 540;
const LEGACY_SETTING_NODE_Y = 1680;
const LEGACY_SETTING_NODE_GAP_X = 380;

export function isFormGeneratorNode(node: ClipCanvasNode): node is ClipCanvasNode & {
  data: { kind: 'form-generator'; formType: ClipCanvasFormType; title: string };
} {
  return node.data.kind === 'form-generator';
}

export function isSettingAssetNode(node: ClipCanvasNode): node is ClipCanvasNode & {
  data: { kind: 'setting-asset'; asset: IProjectAsset; imageUrl?: string; audioUrl?: string };
} {
  return node.data.kind === 'setting-asset';
}

export function isMediaResourceNode(node: ClipCanvasNode): node is ClipCanvasNode & {
  data: {
    kind: 'media-resource';
    mediaType: ClipCanvasResourceMediaType;
    title: string;
    url: string;
    sourceLabel: string;
    prompt?: string;
  };
} {
  return node.data.kind === 'media-resource';
}

export function buildSettingAssetNode(
  videoClipId: string,
  asset: IProjectAsset,
  position: XYPosition,
  actions: ClipCanvasNodeActions,
  imageUrl = asset.conceptImageUrl,
  audioUrl = getAssetVoiceAudioUrl(asset),
  idSuffix: string | number = Date.now(),
): ClipCanvasNode {
  return {
    id: `setting:${videoClipId}:${asset.id}:${idSuffix}`,
    type: 'settingAsset',
    position,
    data: {
      kind: 'setting-asset',
      asset,
      imageUrl,
      audioUrl,
      actions,
    },
  } as ClipCanvasNode;
}

export function getDefaultSettingNodePosition(index: number): XYPosition {
  return {
    x: SETTING_NODE_X,
    y: SETTING_NODE_Y + index * SETTING_NODE_GAP_Y,
  };
}

export function buildMediaResourceNode(
  videoClipId: string,
  mediaType: ClipCanvasResourceMediaType,
  title: string,
  url: string,
  sourceLabel: string,
  position: XYPosition,
  actions: ClipCanvasNodeActions,
  prompt?: string,
  idSuffix: string | number = Date.now(),
): ClipCanvasNode {
  return {
    id: `media-resource:${videoClipId}:${mediaType}:${idSuffix}`,
    type: 'mediaResource',
    position,
    data: {
      kind: 'media-resource',
      mediaType,
      title,
      url,
      sourceLabel,
      prompt,
      actions,
    },
  } as ClipCanvasNode;
}

export function getDefaultMediaResourceNodePosition(index: number): XYPosition {
  return {
    x: MEDIA_RESOURCE_NODE_X,
    y: MEDIA_RESOURCE_NODE_Y + index * MEDIA_RESOURCE_NODE_GAP_Y,
  };
}

export function isLegacyDefaultSettingNodePosition(position: XYPosition): boolean {
  if (position.x === PREVIOUS_SETTING_NODE_X && position.y >= SETTING_NODE_Y && (position.y - SETTING_NODE_Y) % SETTING_NODE_GAP_Y === 0) {
    return true;
  }
  if (position.y !== LEGACY_SETTING_NODE_Y) return false;
  const offsetX = position.x - LEGACY_SETTING_NODE_X;
  return offsetX >= 0 && offsetX % LEGACY_SETTING_NODE_GAP_X === 0;
}
